import { Target, Eye, HeartHandshake, Rocket, CheckCircle2, Building2, Flag, Cpu, Users2 } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { COMPANY } from "../lib/content";

const VALUES = [
    { icon: Target, title: "Mission", desc: "To give every business — from local shops to enterprises — an AI workforce that converts conversations into growth." },
    { icon: Eye, title: "Vision", desc: "A future where no customer enquiry in India goes unanswered, and no business loses growth to manual follow-up." },
    { icon: HeartHandshake, title: "Values", desc: "Transparency, reliability and real business outcomes. We build AI that works for people, not instead of them." },
];

const TRACTION = [
    "Product prototype operational",
    "AI Calling system operational",
    "AI WhatsApp system operational",
    "CRM & automation workflows developed",
    "Lead generation initiated",
    "Product commercialization in progress",
];

export default function Company() {
    return (
        <div className="relative overflow-hidden" data-testid="company-page">
            <div className="glow-orb left-[15%] top-[-5%] h-[360px] w-[480px] bg-blue-600/15" />
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
                <Reveal className="text-center">
                    <Eyebrow>About Us</Eyebrow>
                    <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="company-heading">
                        Real conversations. Real businesses. <span className="cyan-gradient-text">Bigger tomorrow.</span>
                    </h1>
                    <p className="mx-auto mt-5 max-w-3xl text-slate-400">
                        {COMPANY.legal} builds AI employees that call, chat, manage, follow up and automate —
                        so businesses grow without growing headcount.
                    </p>
                </Reveal>

                <div className="mt-14 grid gap-6 md:grid-cols-3">
                    {VALUES.map((v, i) => (
                        <Reveal key={v.title} delay={i * 0.1}>
                            <div className="glow-card h-full rounded-2xl p-7" data-testid={`company-value-${i}`}>
                                <div className="mb-4 inline-flex rounded-xl bg-cyan-500/10 p-3 text-cyan-400">
                                    <v.icon className="h-6 w-6" />
                                </div>
                                <h3 className="font-display text-lg font-bold text-white">{v.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">{v.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <div className="mt-16 grid gap-6 lg:grid-cols-2">
                    <Reveal>
                        <div className="glow-card h-full rounded-2xl p-8" data-testid="company-facts">
                            <h2 className="flex items-center gap-2.5 font-display text-xl font-bold text-white">
                                <Building2 className="h-5 w-5 text-cyan-400" /> Company Details
                            </h2>
                            <dl className="mt-6 space-y-4 text-sm">
                                {[
                                    ["Legal Entity", COMPANY.legal],
                                    ["Brand", "WorkAssign AI — AI Employees for Every Business"],
                                    ["Product", "AI Calling Agent · AI WhatsApp Agent · AI Autopilot · CRM"],
                                    ["Website", "www.workassign.in"],
                                    ["Leadership", "Dharmendra Veer Silawat (Founder & Director)"],
                                ].map(([k, v]) => (
                                    <div key={k} className="flex flex-col gap-1 border-b border-white/5 pb-3 sm:flex-row sm:justify-between">
                                        <dt className="text-slate-500">{k}</dt>
                                        <dd className="text-right font-medium text-slate-200 sm:max-w-[60%]">{v}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="glow-card h-full rounded-2xl p-8" data-testid="company-traction">
                            <h2 className="flex items-center gap-2.5 font-display text-xl font-bold text-white">
                                <Rocket className="h-5 w-5 text-amber-400" /> Current Traction
                            </h2>
                            <p className="mt-2 text-sm text-slate-500">Early validation. Strong foundation. Ready to scale.</p>
                            <ul className="mt-6 space-y-3.5">
                                {TRACTION.map((t) => (
                                    <li key={t} className="flex items-center gap-3 text-sm text-slate-300">
                                        <CheckCircle2 className="h-4.5 w-4.5 h-5 w-5 shrink-0 text-emerald-400" /> {t}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.1} className="mt-16">
                    <div className="flex flex-col items-center gap-4 rounded-3xl border border-white/10 bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-teal-500/10 p-10 text-center">
                        <div className="flex gap-3 text-cyan-400">
                            <Flag className="h-5 w-5" /> <Cpu className="h-5 w-5" /> <Users2 className="h-5 w-5" />
                        </div>
                        <p className="max-w-2xl font-display text-lg font-semibold text-slate-200">
                            From prototype to a global AI workforce — more businesses, more industries, a smarter future.
                        </p>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
